import React from 'react';
import { Play, Headset } from 'lucide-react';
import { useStore } from '../store';
import { soundEngine } from '../audio/soundEngine';

const BG = 'https://images.unsplash.com/photo-1682887524162-306ae119cd48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxhbWF6b24lMjBqdW5nbGUlMjBhZXJpYWwlMjB2aWV3fGVufDB8fHx8MTc4Njg1MTk0MHww&ixlib=rb-4.1.0&q=85';

export default function LandingScreen({ onEnterVR, vrSupported }) {
  const enter = useStore((s) => s.enter);
  const launch = useStore((s) => s.launchSimulation);
  const doLaunch = () => { soundEngine.start(); launch(); };
  const doExplore = () => { soundEngine.start(); enter(); };

  return (
    <div
      data-testid="landing-screen"
      className="absolute inset-0 z-[200] flex flex-col items-center justify-center text-center animate-hud-in pb-16 sm:pb-24"
      style={{
        backgroundImage: `linear-gradient(rgba(5,5,5,0.55), rgba(5,5,5,0.92)), url(${BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="flex flex-col items-center gap-3 mb-6 animate-pulse-gold">
        <img
          src="/tab-seal.png"
          alt="The Academic Boardroom seal"
          className="w-20 h-20 object-contain"
        />
        <span className="font-sans text-xs font-bold uppercase tracking-[0.4em] text-gold">A Spatial Simulation</span>
      </div>

      <h1 className="font-serif font-light tracking-tight text-5xl sm:text-7xl leading-none text-glow">
        <span className="text-white">The Jungle </span>
        <span style={{ color: '#D4AF37' }}>of Bias</span>
      </h1>

      <p className="mt-6 max-w-xl font-sans font-light text-white/70 text-base sm:text-lg leading-relaxed px-6">
        A high-fidelity ecology of ideology. Cross the Amazon between the glowing
        <span className="font-medium" style={{ color: '#3B82F6' }}> Progressive </span> canopy and the fortified
        <span className="font-medium" style={{ color: '#EF4444' }}> Conservative </span> banks — guided by the Harpy Eagle
        through ten contested territories of thought.
      </p>

      <div className="mt-14 flex flex-col items-center gap-5">
        <button
          data-testid="launch-simulation-btn"
          onClick={doLaunch}
          className="group flex items-center gap-3 px-12 py-5 font-extrabold border-2 transition-all duration-300 uppercase tracking-[0.3em] text-base"
          style={{ background: '#D4AF37', color: '#0B1526', borderColor: '#D4AF37', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}
          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 55px rgba(255,241,204,0.95)'; e.currentTarget.style.background = '#eccb58'; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,0.5)'; e.currentTarget.style.background = '#D4AF37'; }}
        >
          <Play className="w-5 h-5" strokeWidth={3} fill="currentColor" />
          Launch Simulation
        </button>

        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button
            data-testid="enter-biome-btn"
            onClick={doExplore}
            className="flex items-center gap-2.5 px-6 py-3 border border-gold/50 bg-black/30 text-gold hover:bg-gold/10 transition-colors duration-300 uppercase tracking-[0.25em] text-[11px] font-bold"
          >
            Explore Freely
          </button>
          <button
            data-testid="enter-vr-btn"
            onClick={() => { soundEngine.start(); onEnterVR(); }}
            disabled={!vrSupported}
            className="flex items-center gap-2.5 px-6 py-3 border border-white/20 bg-black/30 text-white/80 hover:border-gold hover:text-gold transition-colors duration-300 uppercase tracking-[0.25em] text-[11px] font-bold disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Headset className="w-4 h-4" strokeWidth={1.6} />
            {vrSupported ? 'Enter VR' : 'VR Unavailable'}
          </button>
        </div>
      </div>

      <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
        WASD · Steer &amp; Move  ·  Q / E · Altitude  ·  Optimized for Quest 3 &amp; Vision Pro
      </p>
    </div>
  );
}
